import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/auth/auth-context";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import StepIndicator from "@/components/StepIndicator";
import { ROUTE_PATHS, getDashboardDomainUrl } from "@/routing/domainRoutes";

const planNames: Record<string, string> = { starter: "Starter", pro: "Pro", team: "Team" };

export default function Success() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { completePendingSignup } = useAuth();

  const [isEntering, setIsEntering] = useState(false);
  const plan = searchParams.get("plan") || "starter";
  const signupId = searchParams.get("signup");

  const handleEnterWorkspace = async () => {
    if (!signupId) {
      toast({
        title: "Missing signup reference",
        description: "Please return to pricing and try again.",
        variant: "destructive",
      });
      return;
    }

    setIsEntering(true);
    try {
      await completePendingSignup(signupId);
      sessionStorage.removeItem("workscope-signup-draft");
      window.location.replace(getDashboardDomainUrl(ROUTE_PATHS.dashboardHome));
    } catch (error) {
      toast({
        title: "Workspace not ready yet",
        description:
          error instanceof Error ? error.message : "If payment just completed, wait a few seconds and try again.",
        variant: "destructive",
      });
      setIsEntering(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-background">
        <StepIndicator currentStep={4} />
        <div className="container mx-auto px-4 pb-20 lg:px-8">
          <div className="mx-auto max-w-2xl rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
            <h1 className="text-3xl font-bold text-primary">Welcome to WorkScope</h1>
            <p className="mt-3 text-muted-foreground">
              {t("success.setupAlmostComplete", { plan: planNames[plan] || "Starter" })}
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button
                onClick={handleEnterWorkspace}
                disabled={isEntering}
                className="bg-accent text-accent-foreground hover:bg-accent/90"
              >
                {isEntering ? "Entering..." : t("success.enterWorkspace")}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
